import { forwardRef, useImperativeHandle } from "react";
import {
  FiChevronDown,
  FiChevronLeft,
  FiClock,
  FiRefreshCw,
  FiRotateCcw,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import {
  type CatalogItemOverrides,
  DesktopCatalogItem,
} from "@/features/catalog/components/desktop/DesktopCatalogItem";
import { HistoryModeToggle } from "@/features/history/components/HistoryModeToggle";
import {
  DESKTOP_HISTORY_ITEM_IMAGE_SIZE,
  DESKTOP_HISTORY_ITEM_TEXT_LINES,
} from "@/features/history/desktopHistorySidebar";
import { historyThreadKey } from "@/features/history/historyIdentity";
import type { HistorySidebarEvent } from "@/features/history/historySidebarMediator";
import { useHistorySidebarPresenter } from "@/features/history/useHistorySidebarPresenter";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { RefreshStatus } from "@/shared/ui/feedback";
import { Button } from "@/shared/ui/form";

/**
 * サイドバー表示は旧実装(本文2行固定・同一タブ遷移・未読バッジ常時表示)の
 * 挙動を維持する。カタログ共通設定(useCatalogStore)への連動は行わない。
 * クリック時のaddViewedは遷移先(DesktopThreadView)側で記録されるため、
 * サイドバー表示中に一覧の並びが書き換わらないよう無効化する。
 */
const SIDEBAR_ITEM_OVERRIDES: CatalogItemOverrides = {
  imageSize: DESKTOP_HISTORY_ITEM_IMAGE_SIZE,
  textLines: DESKTOP_HISTORY_ITEM_TEXT_LINES,
  openInNewTab: false,
  showCount: true,
  showUnreadCount: true,
  showFooter: false,
  disableViewedTracking: true,
};

export interface DesktopHistorySidebarHandle {
  toggle: () => void;
}

interface DesktopHistorySidebarProps {
  showClosedHandle?: boolean;
}

interface DesktopHistorySidebarViewProps extends DesktopHistorySidebarProps {
  readonly config: ReturnType<typeof useHistorySidebarPresenter>["config"];
  readonly historyModeLabel: string;
  readonly historyItemsCount: number;
  readonly historyScopeSlugs: readonly string[];
  readonly threads: ReturnType<typeof useHistorySidebarPresenter>["threads"];
  readonly contentState: ReturnType<
    typeof useHistorySidebarPresenter
  >["contentState"];
  readonly isFetching: boolean;
  readonly isManualRefreshing: boolean;
  readonly onEvent: (event: HistorySidebarEvent) => void;
}

export const DesktopHistorySidebarView: React.FunctionComponent<
  DesktopHistorySidebarViewProps
> = ({
  showClosedHandle = true,
  config,
  historyModeLabel,
  historyItemsCount,
  historyScopeSlugs,
  threads,
  contentState,
  isFetching,
  isManualRefreshing,
  onEvent,
}: DesktopHistorySidebarViewProps): React.ReactElement => {
  const sidebar = !config.sidebarVisible ? (
    showClosedHandle ? (
      <Button
        variant="secondary"
        icon={<FiClock aria-hidden="true" size={16} />}
        className="fixed left-0 top-1/2 z-40 -translate-y-1/2 flex-col gap-1 rounded-l-none px-2 py-3 shadow-md"
        onClick={(): void =>
          onEvent({
            type: "history/sidebar-visibility-requested",
            visible: true,
          })
        }
        aria-label="履歴サイドバーを開く"
      >
        履歴
      </Button>
    ) : null
  ) : (
    <aside aria-label="PC履歴" className="desktop-history-sidebar">
      <header className="desktop-history-sidebar-heading">
        <h2>
          <FiRotateCcw aria-hidden="true" />
          履歴
        </h2>
        <button
          type="button"
          className="desktop-history-sidebar-close"
          onClick={(): void =>
            onEvent({
              type: "history/sidebar-visibility-requested",
              visible: false,
            })
          }
          aria-label="履歴サイドバーを閉じる"
        >
          <FiChevronLeft aria-hidden="true" />
        </button>
      </header>
      <HistoryModeToggle
        mode={config.historyMode}
        onEvent={onEvent}
        className="desktop-history-sidebar-mode-toggle"
      />
      <fieldset className="desktop-history-sidebar-controls">
        <legend className="sr-only">履歴の表示操作</legend>
        <label className="desktop-history-sidebar-sort">
          ソート:
          <select
            aria-label={`${historyModeLabel}の並び順`}
            value={config.sortByNewRes ? "newReplies" : "viewed"}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>): void =>
              onEvent({
                type: "history/sidebar-sort-changed",
                sortByNewRes: event.currentTarget.value === "newReplies",
              })
            }
          >
            <option value="viewed">
              {config.historyMode === "posted" ? "書込順" : "閲覧順"}
            </option>
            <option value="newReplies">新着順</option>
          </select>
          <FiChevronDown aria-hidden="true" />
        </label>
        <button
          type="button"
          className="desktop-history-sidebar-refresh"
          disabled={isFetching}
          onClick={(): void =>
            onEvent({ type: "history/sidebar-refresh-requested" })
          }
        >
          <FiRefreshCw aria-hidden="true" />
          {isFetching ? "更新中" : "更新"}
        </button>
      </fieldset>
      <section aria-label="履歴項目" className="desktop-history-sidebar-scroll">
        {contentState === "empty" ? (
          <p className="desktop-history-sidebar-empty">
            {historyModeLabel}はありません
          </p>
        ) : contentState === "loading" ? (
          <p className="desktop-history-sidebar-empty">
            {historyModeLabel}を読み込み中...
          </p>
        ) : contentState === "error" ? (
          <p className="desktop-history-sidebar-empty">
            {historyModeLabel}の読み込みに失敗しました
          </p>
        ) : contentState === "noExistingThreads" ? (
          <p className="desktop-history-sidebar-empty">
            現在存在するスレッドはありません
          </p>
        ) : (
          <ol className="desktop-history-sidebar-list">
            {threads.map((thread) => (
              <li key={historyThreadKey(thread)}>
                <DesktopCatalogItem
                  thread={thread}
                  isNew={false}
                  overrides={SIDEBAR_ITEM_OVERRIDES}
                />
                <button
                  type="button"
                  className="desktop-history-sidebar-remove"
                  onClick={(): void =>
                    onEvent({
                      type: "history/sidebar-item-remove-requested",
                      mode: config.historyMode,
                      boardSlug: thread.boardSlug,
                      threadId: thread.threadId,
                    })
                  }
                  aria-label={`${historyModeLabel}から削除: ${thread.id}`}
                >
                  <FiX aria-hidden="true" />
                </button>
              </li>
            ))}
          </ol>
        )}
      </section>
      <footer className="desktop-history-sidebar-footer">
        <button
          type="button"
          onClick={(): void =>
            onEvent({
              type: "history/sidebar-clear-requested",
              mode: config.historyMode,
              boardSlugs: historyScopeSlugs,
            })
          }
          disabled={historyItemsCount === 0}
        >
          <FiTrash2 aria-hidden="true" />
          履歴をすべて削除
        </button>
      </footer>
    </aside>
  );

  return (
    <>
      {sidebar}
      <RefreshStatus isRefreshing={isManualRefreshing} />
    </>
  );
};

export const DesktopHistorySidebar: React.ForwardRefExoticComponent<
  DesktopHistorySidebarProps & React.RefAttributes<DesktopHistorySidebarHandle>
> = forwardRef(function DesktopHistorySidebar(
  { showClosedHandle = true }: DesktopHistorySidebarProps,
  ref,
): React.ReactNode {
  const presentation = useHistorySidebarPresenter();
  useImperativeHandle(ref, () => ({ toggle: presentation.toggle }), [
    presentation.toggle,
  ]);

  return (
    <ComponentScope node={presentation.node}>
      <DesktopHistorySidebarView
        showClosedHandle={showClosedHandle}
        config={presentation.config}
        historyModeLabel={presentation.historyModeLabel}
        historyItemsCount={presentation.historyItemsCount}
        historyScopeSlugs={presentation.historyScopeSlugs}
        threads={presentation.threads}
        contentState={presentation.contentState}
        isFetching={presentation.isFetching}
        isManualRefreshing={presentation.isManualRefreshing}
        onEvent={presentation.onEvent}
      />
    </ComponentScope>
  );
});
